// JobClose AI · Slice 2U · Support Account Summary page.
// Read-only. All data is loaded via /api/support/* endpoints which
// enforce require_admin, tenant scoping, and safe projections.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCcw, ShieldCheck, EyeOff, AlertTriangle,
  Building2, Mail, Phone, Globe, ClipboardCheck, User as UserIcon,
  CalendarDays, Wrench, BadgeCheck, Files, MessageSquare,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { supportApi } from "@/lib/supportApi";
import { formatDate } from "@/lib/api";

const KV = ({ label, value, testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="text-[10px] uppercase tracking-wider text-[var(--jc-text-soft)]">{label}</span>
    <span className="text-sm text-[var(--jc-text)]">{value ?? "—"}</span>
  </div>
);

const UnknownPill = ({ label }) => (
  <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-[var(--jc-bg)] px-2 py-0.5 text-[var(--jc-text-soft)]">
    <EyeOff size={10} /> {label}
  </span>
);

const Card = ({ testid, title, right, children, className = "" }) => (
  <section className={`jc-card p-5 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display text-base">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

export default function SupportAccountSummary() {
  const { t } = useLang();
  const { user: adminUser } = useAuth();
  const { uid } = useParams();
  const [account, setAccount] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionKnown, setSubscriptionKnown] = useState(false);
  const [aggregates, setAggregates] = useState(null);
  const [projects, setProjects] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectSummary, setProjectSummary] = useState(null);
  const [projectSummaryLoading, setProjectSummaryLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [acc, sub, agg, projs, diag, tl] = await Promise.all([
        supportApi.account(uid),
        supportApi.subscription(uid),
        supportApi.aggregates(uid),
        supportApi.projects(uid, { limit: 25 }),
        supportApi.diagnostics(uid),
        supportApi.timeline(uid),
      ]);
      setAccount(acc.data);
      setSubscription(sub.data.subscription);
      setSubscriptionKnown(true);
      setAggregates(agg.data);
      setProjects(projs.data);
      setDiagnostics(diag.data);
      setTimeline(tl.data.milestones || []);
      setRefreshedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail?.error || err?.message || "load_failed");
    }
  }, [uid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openProject = async (pid) => {
    setSelectedProjectId(pid);
    setProjectSummary(null);
    setProjectSummaryLoading(true);
    try {
      const r = await supportApi.projectSummary(uid, pid);
      setProjectSummary(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail?.error || "project_summary_failed");
    } finally {
      setProjectSummaryLoading(false);
    }
  };

  const identity = account?.identity;
  const profile = account?.pricing_profile_summary;

  const shareCounts = aggregates?.sharing_state_counts || {};

  const timelineSorted = useMemo(
    () => (timeline || []).slice().sort((a, b) => String(a.at).localeCompare(String(b.at))),
    [timeline],
  );

  return (
    <DashboardLayout
      title={t("support.account.title")}
      subtitle={identity ? `${identity.company_name || identity.full_name || identity.email}` : t("support.account.loading")}
    >
      {/* Persistent tenant-context header — always visible. */}
      <section
        data-testid="support-tenant-context"
        className="jc-card p-4 mb-6 border-l-4 border-l-[var(--jc-blue)] bg-[var(--jc-surface)]"
      >
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="overline text-[var(--jc-text-soft)]">
              {t("support.tenant_context.supporting")}
            </div>
            <div className="font-display text-lg leading-tight" data-testid="support-tenant-company">
              {identity?.company_name || identity?.full_name || t("support.unknown")}
            </div>
            <div className="text-xs text-[var(--jc-text-soft)]" data-testid="support-tenant-owner">
              {t("support.tenant_context.owner")}: {identity?.full_name || "—"}
            </div>
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] space-y-1">
            <div>
              <span className="overline text-[var(--jc-text-soft)]">
                {t("support.tenant_context.tenant_id")}
              </span>
              <div className="font-mono text-[11px]" data-testid="support-tenant-id">{uid}</div>
            </div>
            <div>
              <span className="overline text-[var(--jc-text-soft)]">
                {t("support.tenant_context.viewed_by")}
              </span>
              <div data-testid="support-tenant-viewer">{adminUser?.full_name || adminUser?.email}</div>
            </div>
          </div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            <div
              className="inline-flex items-center gap-1 rounded-full bg-[var(--jc-bg)] px-2 py-0.5"
              data-testid="support-read-only-badge"
            >
              <ShieldCheck size={10} /> {t("support.read_only")}
            </div>
            {refreshedAt && (
              <div className="mt-1" data-testid="support-refreshed-at">
                {t("support.refreshed_at")}: {refreshedAt.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Link
            to="/app/support"
            className="jc-btn-secondary text-xs inline-flex items-center gap-1"
            data-testid="support-back-to-search-btn"
          >
            <ArrowLeft size={12} /> {t("support.back_to_search")}
          </Link>
          <button
            type="button"
            onClick={loadAll}
            className="jc-btn-secondary text-xs inline-flex items-center gap-1"
            data-testid="support-refresh-btn"
          >
            <RefreshCcw size={12} /> {t("support.refresh")}
          </button>
        </div>
      </section>

      {error && (
        <div
          data-testid="support-error"
          className="jc-card p-3 border-l-4 border-l-[var(--jc-red)] mb-4 text-sm text-[var(--jc-red)]"
        >
          <AlertTriangle size={14} className="inline mr-1" /> {error}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* A. Account identity */}
        <Card testid="support-account-identity" title={t("support.account.identity")}>
          <div className="grid grid-cols-2 gap-4">
            <KV label={t("support.identity.full_name")} value={identity?.full_name} testid="support-identity-name" />
            <KV label={t("support.identity.company")} value={identity?.company_name} testid="support-identity-company" />
            <KV label={t("support.identity.email")} value={identity?.email} testid="support-identity-email" />
            <KV label={t("support.identity.phone")} value={identity?.phone} testid="support-identity-phone" />
            <KV label={t("support.identity.created")} value={formatDate(identity?.created_at)} testid="support-identity-created" />
            <KV label={t("support.identity.preferred_lang")} value={identity?.preferred_lang || t("support.unknown")} testid="support-identity-lang" />
            <KV
              label={t("support.identity.onboarding")}
              value={
                identity?.setup_completed === true
                  ? t("support.search.onboarding_complete")
                  : identity?.setup_completed === false
                  ? t("support.search.onboarding_incomplete")
                  : t("support.unknown")
              }
              testid="support-identity-onboarding"
            />
            <KV
              label={t("support.identity.trades")}
              value={
                Array.isArray(profile?.trades) && profile.trades.length > 0
                  ? profile.trades.map(tr => (typeof tr === "string" ? tr : tr.name || tr.slug || "")).filter(Boolean).join(", ")
                  : (profile ? t("support.identity.trades_none") : t("support.unknown"))
              }
              testid="support-identity-trades"
            />
          </div>
        </Card>

        {/* B. Subscription */}
        <Card testid="support-subscription-summary" title={t("support.account.subscription")}>
          {!subscriptionKnown ? (
            <UnknownPill label={t("support.unknown")} />
          ) : subscription === null ? (
            <div className="text-sm text-[var(--jc-text-soft)]" data-testid="support-subscription-none">
              {t("support.subscription.none")}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <KV label={t("support.subscription.tier")} value={subscription.tier} testid="support-subscription-tier" />
              <KV label={t("support.subscription.status")} value={subscription.status} testid="support-subscription-status" />
              <KV label={t("support.subscription.billing_cycle")} value={subscription.billing_cycle} testid="support-subscription-cycle" />
              <KV label={t("support.subscription.current_period_end")} value={formatDate(subscription.current_period_end) || t("support.unknown")} testid="support-subscription-period-end" />
              <KV label={t("support.subscription.trial_end")} value={formatDate(subscription.trial_end) || t("support.unknown")} testid="support-subscription-trial-end" />
              <KV
                label={t("support.subscription.launch_offer")}
                value={
                  subscription.launch_offer === true
                    ? t("support.yes")
                    : subscription.launch_offer === false
                    ? t("support.no")
                    : t("support.unknown")
                }
                testid="support-subscription-launch-offer"
              />
              <KV label={t("support.subscription.cancel_at_period_end")} value={subscription.cancel_at_period_end ? t("support.yes") : t("support.no")} testid="support-subscription-cancel-at" />
              <KV label={t("support.subscription.updated")} value={formatDate(subscription.updated_at)} testid="support-subscription-updated" />
            </div>
          )}
        </Card>

        {/* C. Aggregates */}
        <Card testid="support-project-aggregates" title={t("support.account.aggregates")}>
          {!aggregates ? (
            <UnknownPill label={t("support.unknown")} />
          ) : (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <KV label={t("support.aggregates.projects_total")} value={aggregates.projects.total} />
              <KV label={t("support.aggregates.projects_draft")} value={aggregates.projects.draft} />
              <KV label={t("support.aggregates.projects_active")} value={aggregates.projects.active} />
              <KV label={t("support.aggregates.projects_closed")} value={aggregates.projects.closed} />
              <KV label={t("support.aggregates.estimates")} value={aggregates.estimates.total} />
              <KV label={t("support.aggregates.proposals")} value={aggregates.proposals.total} />
              <KV label={t("support.aggregates.contracts")} value={aggregates.contracts.total} />
              <KV label={t("support.aggregates.invoices_total")} value={aggregates.invoices.total} />
              <KV label={t("support.aggregates.invoices_paid")} value={aggregates.invoices.paid} />
              <KV label={t("support.aggregates.invoices_unpaid")} value={aggregates.invoices.unpaid} />
              <KV label={t("support.aggregates.work_areas")} value={aggregates.work_areas.total} />
              <KV label={t("support.aggregates.voice_failed")} value={aggregates.voice_notes.failed} />
              <KV label={t("support.aggregates.dc_red")} value={aggregates.double_check.red_count_across_estimates} />
              <KV label={t("support.aggregates.dc_yellow")} value={aggregates.double_check.yellow_count_across_estimates} />
              <KV label={t("support.aggregates.dc_ran")} value={aggregates.double_check.estimates_with_double_check} />
            </div>
          )}
        </Card>

        {/* D. Sharing summary */}
        <Card testid="support-sharing-summary" title={t("support.account.sharing")}>
          {!aggregates ? (
            <UnknownPill label={t("support.unknown")} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-[var(--jc-text-soft)]">
                <tr className="border-b border-[var(--jc-border)]">
                  <th className="text-left py-1">{t("support.sharing.kind")}</th>
                  <th className="text-left py-1">private</th>
                  <th className="text-left py-1">approved</th>
                  <th className="text-left py-1">shared</th>
                  <th className="text-left py-1">revoked</th>
                </tr>
              </thead>
              <tbody>
                {["proposals", "contracts", "closeout_packets"].map((k) => (
                  <tr key={k} data-testid={`support-sharing-row-${k}`} className="border-b border-[var(--jc-border)]">
                    <td className="py-1 font-mono text-[11px]">{k}</td>
                    <td className="py-1">{shareCounts[k]?.private ?? "—"}</td>
                    <td className="py-1">{shareCounts[k]?.approved ?? "—"}</td>
                    <td className="py-1">{shareCounts[k]?.shared ?? "—"}</td>
                    <td className="py-1">{shareCounts[k]?.revoked ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-2 text-[11px] text-[var(--jc-text-soft)] flex items-center gap-1">
            <EyeOff size={10} /> {t("support.sharing.tokens_hidden")}
          </div>
        </Card>
      </div>

      {/* E. Projects list */}
      <Card testid="support-projects-list" title={t("support.account.projects")} className="mt-6">
        {!projects ? (
          <UnknownPill label={t("support.unknown")} />
        ) : projects.count === 0 ? (
          <div className="text-sm text-[var(--jc-text-soft)]">{t("support.projects.none")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--jc-text-soft)]">
              <tr className="border-b border-[var(--jc-border)]">
                <th className="text-left py-1">{t("support.projects.customer")}</th>
                <th className="text-left py-1">{t("support.projects.job_type")}</th>
                <th className="text-left py-1">{t("support.projects.address")}</th>
                <th className="text-left py-1">{t("support.projects.status")}</th>
                <th className="text-left py-1">{t("support.projects.updated")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.projects.map((p) => (
                <tr key={p.id} data-testid={`support-project-row-${p.id}`} className="border-b border-[var(--jc-border)] hover:bg-[var(--jc-bg)]">
                  <td className="py-1">{p.customer_name || "—"}</td>
                  <td className="py-1">{p.job_type || "—"}</td>
                  <td className="py-1 truncate max-w-[280px]">{p.job_address || "—"}</td>
                  <td className="py-1">{p.status || "—"}</td>
                  <td className="py-1">{formatDate(p.updated_at)}</td>
                  <td className="py-1 text-right">
                    <button
                      type="button"
                      className="text-xs jc-btn-secondary px-3 py-1"
                      data-testid={`support-open-project-${p.id}`}
                      onClick={() => openProject(p.id)}
                    >
                      {t("support.projects.diagnose")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* F. Selected project drill-down */}
      {selectedProjectId && (
        <Card
          testid="support-project-drilldown"
          title={t("support.account.project_summary")}
          right={
            <button
              className="text-xs text-[var(--jc-text-soft)]"
              onClick={() => { setSelectedProjectId(null); setProjectSummary(null); }}
              data-testid="support-project-drilldown-close"
            >
              {t("support.close")}
            </button>
          }
          className="mt-6"
        >
          {projectSummaryLoading && <UnknownPill label={t("support.loading")} />}
          {projectSummary && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <KV label={t("support.projects.customer")} value={projectSummary.project?.customer_name} />
                <KV label={t("support.projects.address")} value={projectSummary.project?.job_address} />
                <KV label={t("support.projects.status")} value={projectSummary.project?.status} />
                <KV label={t("support.aggregates.work_areas")} value={projectSummary.work_areas_count} />
                <KV label={t("support.aggregates.voice_failed")} value={projectSummary.voice_notes?.failed} />
                <KV label={t("support.projects.photos_count")} value={projectSummary.photos_count} />
              </div>

              <div>
                <div className="overline mb-1">{t("support.projects.estimates")}</div>
                {projectSummary.estimates?.length ? (
                  <ul className="space-y-1" data-testid="support-project-estimates">
                    {projectSummary.estimates.map((e) => (
                      <li key={e.id} className="text-xs bg-[var(--jc-bg)] rounded px-2 py-1">
                        <span className="font-mono">{e.id.slice(0, 8)}</span> · lines {e.line_items_count} · unpriced {e.unpriced_line_items_count} · DC {e.double_check ? `${e.double_check.red_count}R / ${e.double_check.yellow_count}Y` : t("support.unknown")}
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-[var(--jc-text-soft)]">—</div>}
              </div>

              <div>
                <div className="overline mb-1">{t("support.projects.docs")}</div>
                <div className="text-xs grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div data-testid="support-drilldown-proposals">
                    <b>{t("support.aggregates.proposals")}:</b> {(projectSummary.proposals || []).map((p) => `${(p.id||"").slice(0,6)} [${p.sharing_state}]`).join(", ") || "—"}
                  </div>
                  <div data-testid="support-drilldown-contracts">
                    <b>{t("support.aggregates.contracts")}:</b> {(projectSummary.contracts || []).map((c) => `${(c.id||"").slice(0,6)} [${c.sharing_state}${c.status ? ` / ${c.status}` : ""}]`).join(", ") || "—"}
                  </div>
                  <div data-testid="support-drilldown-invoices">
                    <b>{t("support.aggregates.invoices_total")}:</b> {(projectSummary.invoices || []).map((i) => `${(i.id||"").slice(0,6)} [${i.payment_status}]`).join(", ") || "—"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* G. Diagnostics */}
      <Card testid="support-diagnostics" title={t("support.account.diagnostics")} className="mt-6">
        {!diagnostics ? (
          <UnknownPill label={t("support.unknown")} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <KV label={t("support.diagnostics.email_total")} value={diagnostics.email_outbox.total} />
            <KV label={t("support.diagnostics.email_failed")} value={diagnostics.email_outbox.failed_or_error} />
            <KV label={t("support.diagnostics.email_queued")} value={diagnostics.email_outbox.queued_or_pending} />
            <KV label={t("support.diagnostics.voice_failed")} value={diagnostics.voice_notes.failed} />
            <KV label={t("support.diagnostics.voice_pending")} value={diagnostics.voice_notes.pending} />
            <KV label={t("support.diagnostics.login_failed")} value={diagnostics.auth_events.login_failed_count} />
            <KV label={t("support.diagnostics.login_success")} value={diagnostics.auth_events.login_success_count} />
            <KV label={t("support.diagnostics.password_failed")} value={diagnostics.auth_events.password_change_failed_count} />
          </div>
        )}
      </Card>

      {/* H. Activity timeline */}
      <Card testid="support-timeline" title={t("support.account.timeline")} className="mt-6">
        {!timeline ? (
          <UnknownPill label={t("support.unknown")} />
        ) : timeline.length === 0 ? (
          <div className="text-sm text-[var(--jc-text-soft)]">{t("support.timeline.none")}</div>
        ) : (
          <ol className="space-y-2" data-testid="support-timeline-list">
            {timelineSorted.map((m) => (
              <li
                key={`${m.key}-${m.at}`}
                data-testid={`support-timeline-item-${m.key}`}
                className="flex items-start gap-3 text-sm"
              >
                <CalendarDays size={14} className="text-[var(--jc-text-soft)] mt-0.5" />
                <div className="flex-1">
                  <div className="text-[var(--jc-text)]">{t(`support.timeline.milestone.${m.key}`)}</div>
                  <div className="text-xs text-[var(--jc-text-soft)]">{formatDate(m.at)}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <div className="mt-6 text-[11px] text-[var(--jc-text-soft)] flex items-center gap-1" data-testid="support-footer-note">
        <EyeOff size={10} /> {t("support.footer_no_mutations")}
      </div>
    </DashboardLayout>
  );
}
